
import {ø, keep} from "../../util/objects.js";
import * as utc from "../../util/utc.js";
import * as clock from "../../util/clock.js";
import {scalarProduct} from "../scalarProduct.js";
import * as sources from "../sources.js";
import {newProduct} from "../productUtils.js";
import {tr} from "../../ux/translations.js";
import {createUnitDescriptors} from "../../util/units.js";
import {gaiaPath} from "../gaia.js";
import {createSSTAPalette} from "../../palette/ssta.js";
import {createSSTWithIcePalette} from "../../palette/sstice.js";


const SST = "sea_surface_temp";
const SSTA = "sea_surface_temp_anomaly";

let palettes = {};

function recipeFor(attr, name) {
    if (name === SST) {
        return attr.hd_enabled ?
            {tag: SST, res: "0.1", selector: /analysed_sst/} :
            {tag: SST, res: "0.5", selector: /analysed_sst/};
    }
    if (name === SSTA) {
        return attr.hd_enabled ?
            {tag: SSTA, res: "0.25", selector: /sst_anomaly/} :
            {tag: SSTA, res: "0.5",  selector: /sst_anomaly/};
    }
}

export function ostiaValidTime(time_cursor) {
    let parts;
    if (time_cursor === "current") {
        // Each day, SST data for the previous day is made available. So "current" means yesterday.
        const now = keep(utc.parts(clock.now()), "year", "month", "day");
        parts = utc.add(now, {day: -1});
    } else {
        parts = ø(time_cursor);
    }
    return parts;
}

export function ostiaPath(attr, name) {
    // 2019/12/20191215-sst-ostia-0.1.epak
    // 2019/12/20191215-ssta-ostia-0.25.epak
    // current/current-sst-ostia-0.1.epak

    const recipe = recipeFor(attr, name);
    const isCurrent = attr.time_cursor === "current";
    const dir = isCurrent ? "current" : utc.print(attr.time_cursor, "{YYYY}/{MM}");
    const timestamp = isCurrent ? "current" : utc.print(attr.time_cursor, "{YYYY}{MM}{DD}");
    const file = `${timestamp}-${recipe.tag}-ostia-${recipe.res}.epak`;
    return gaiaPath("data/ostia", dir, file);
}

function newOstiaProduct(attr) {
    const validTime = ostiaValidTime(attr.time_cursor);
    const stepper = step => function() {
        return ostiaValidTime(utc.add(this.validTime(), step));
    };
    return {
        sourceHTML: sources.ostia,
        validTime: () => ø(validTime),
        prev2: stepper({day: -5}),
        prev1: stepper({day: -1}),
        next1: stepper({day: +1}),
        next2: stepper({day: +5}),
        navLabels: {
            prev2: `-5 ${tr`days`}`,
            prev1: `-1 ${tr`day`}`,
            next1: `+1 ${tr`day`}`,
            next2: `+5 ${tr`days`}`,
        },
    };
}

export function createOstiaSST(attr) {
    return ø(newProduct(), newOstiaProduct(attr), {
        type: SST,
        descriptionHTML: {
            name: tr`Sea Surface Temperature`,
            qualifier: "",
        },
        paths: [ostiaPath(attr, SST)],
        builder: file => scalarProduct(file, /analysed_sst/, {hasMissing: true}),
        unitDescriptors: createUnitDescriptors({
            "°C": {convert: x => x - 273.15,       precision: 1},
            "°F": {convert: x => x * 9/5 - 459.67, precision: 1},
            "K":  {convert: x => x,                precision: 1},
        }),
        scale: palettes.sst ??= createSSTWithIcePalette(),
        alpha: {single: 200, animated: 132},
    });
}

export function createOstiaSSTA(attr) {
    return ø(newProduct(), newOstiaProduct(attr), {
        type: SSTA,
        descriptionHTML: {
            name: tr`Sea Surface Temperature Anomaly`,
            qualifier: "",
        },
        paths: [ostiaPath(attr, SSTA)],
        builder: file => scalarProduct(file, /sst_anomaly/, {hasMissing: true}),
        unitDescriptors: createUnitDescriptors({
            "°C": {convert: x => x,       precision: 1},
            "°F": {convert: x => x * 9/5, precision: 1},
            "K":  {convert: x => x,       precision: 1},
        }),
        scale: palettes.ssta ??= createSSTAPalette(),
    });
}
